/** Whether a recorded clip holds any sound at all — a dead or muted microphone. See ADR-0014. */
import { VOICE_CAPTURE } from '../config/constants';

export type ClipDecoder = (clip: Blob) => Promise<Float32Array[]>;

const DECODE_CHANNELS = 1;
const DECODE_FRAMES = 1;
const DECODE_SAMPLE_RATE = 44100;
const DBFS_PER_DECADE = 20;

/** Loudest sample across every channel, in dB relative to full scale. −Infinity for pure zeros. */
export function peakDbfs(channels: Float32Array[]): number {
  let peak = 0;
  for (const samples of channels) {
    for (let i = 0; i < samples.length; i++) {
      const magnitude = Math.abs(samples[i]);
      if (magnitude > peak) peak = magnitude;
    }
  }
  return DBFS_PER_DECADE * Math.log10(peak);
}

export function isSilentPeak(
  peak: number,
  threshold: number = VOICE_CAPTURE.SILENT_TAKE_PEAK_DBFS,
): boolean {
  return peak < threshold;
}

type WindowWithOfflineAudio = typeof window & {
  webkitOfflineAudioContext?: typeof OfflineAudioContext;
};

const decodeInBrowser: ClipDecoder = async (clip) => {
  const Ctor =
    window.OfflineAudioContext || (window as WindowWithOfflineAudio).webkitOfflineAudioContext;
  if (!Ctor) throw new Error('OfflineAudioContext not supported');
  const context = new Ctor(DECODE_CHANNELS, DECODE_FRAMES, DECODE_SAMPLE_RATE);
  const buffer = await context.decodeAudioData(await clip.arrayBuffer());
  return Array.from({ length: buffer.numberOfChannels }, (_, i) => buffer.getChannelData(i));
};

/** True only for a clip that was MEASURED and found silent; anything unmeasurable is not. */
export async function isSilentTake(
  clip: Blob,
  decode: ClipDecoder = decodeInBrowser,
): Promise<boolean> {
  try {
    const channels = await decode(clip);
    if (channels.length === 0) return false;
    return isSilentPeak(peakDbfs(channels));
  } catch {
    return false;
  }
}
